import React, { useState, useEffect } from 'react';
import TabNavigation from '../../components/ui/TabNavigation';
import Icon from '../../components/AppIcon';
import CompatibilityScoreCard from './components/CompatibilityScoreCard';
import IntimacyTrendsChart from './components/IntimacyTrendsChart';
import PreferenceAlignmentChart from './components/PreferenceAlignmentChart';
import RecommendationsPanel from './components/RecommendationsPanel';
import AdvancedMetrics from './components/AdvancedMetrics';
import { generateAnalyticsInsights } from '../../services/anthropicService';
import notificationService from '../../services/notificationService';

const AIAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Mock data removed - ready for live data integration
  const compatibilityScores = [];
  const intimacyTrendsData = [];
  const preferenceAlignmentData = [];
  const advancedMetrics = {
    satisfactionTrend: 0,
    satisfactionChange: 0,
    encounterSuccessRate: 0,
    successRateChange: 0,
    predictedCompatibility: 0,
    compatibilityChange: 0,
    relationshipHealth: 0,
    healthChange: 0
  };

  const handleGenerateInsights = async () => {
    setIsGenerating(true);
    try {
      // In production, this would analyze actual couple data
      const mockAnalysisData = {
        compatibilityScores,
        intimacyTrends: intimacyTrendsData,
        preferenceAlignment: preferenceAlignmentData,
        timeRange
      };
      
      const insights = await generateAnalyticsInsights(mockAnalysisData);
      setAiRecommendations(insights);
      setRefreshKey(prev => prev + 1);
      
      notificationService?.notifyAISuggestion(
        'analytics_insights',
        'high',
        'New personalized relationship insights generated'
      );
    } catch (error) {
      // Only log unexpected errors - skip internal API errors (402, 401, 429, etc.)
      const isInternalError = error?.isInternal || 
                              error?.statusCode === 402 || 
                              error?.statusCode === 401 || 
                              error?.statusCode === 429 ||
                              error?.statusCode === 403 ||
                              error?.statusCode === 500 ||
                              error?.statusCode === 529;
      
      if (!isInternalError) {
        console.error('Failed to generate insights:', error);
      }
      
      notificationService?.notifyAISuggestion(
        'analytics_error',
        'medium',
        'Unable to generate insights. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportReport = () => {
    // In production, this would generate a PDF or downloadable report
    notificationService?.notifyAISuggestion(
      'export_report',
      'medium',
      'Analytics report exported successfully'
    );
  };

  useEffect(() => {
    // Auto-generate insights on mount
    handleGenerateInsights();
  }, []);

  return (
    <div className="min-h-screen bg-background pb-20 lg:pb-8">
      <TabNavigation />
      
      <div className="pt-20 lg:pt-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                AI Analytics Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                Claude-powered insights for deepening your connection
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="px-4 py-2 bg-card border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
              
              <button
                onClick={handleExportReport}
                className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md transition-therapeutic text-sm font-medium"
              >
                <Icon name="Download" size={18} color="currentColor" />
                <span className="hidden md:inline">Export</span>
              </button>
              
              <button
                onClick={handleGenerateInsights}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-therapeutic text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon 
                  name={isGenerating ? 'Loader2' : 'Sparkles'} 
                  size={18} 
                  color="currentColor"
                  className={isGenerating ? 'animate-spin' : ''}
                />
                <span className="hidden md:inline">
                  {isGenerating ? 'Generating...' : 'Generate Insights'}
                </span>
              </button>
            </div>
          </div>

          {/* Compatibility Score Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {compatibilityScores?.map((score) => (
              <CompatibilityScoreCard key={score?.id} score={score} />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <IntimacyTrendsChart data={intimacyTrendsData} timeRange={timeRange} />
            <PreferenceAlignmentChart data={preferenceAlignmentData} />
          </div>

          {/* Advanced Metrics */}
          <AdvancedMetrics metrics={advancedMetrics} />

          {/* AI Recommendations Panel */}
          {aiRecommendations && (
            <RecommendationsPanel 
              recommendations={aiRecommendations} 
              refreshKey={refreshKey}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAnalyticsDashboard;